/**
 * Generate a consistent ID for a heading
 * @param {string} text - The heading text
 * @returns {string} - The generated ID
 */
const generateHeadingId = (text) => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Scroll to element with header offset
 * @param {string} id - The element ID to scroll to
 */
const scrollToElement = (id) => {
  const element = document.getElementById(id);
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    const url = new URL(window.location.href);
    url.hash = id;
    window.history.pushState({}, '', url.toString());
  }
};

/**
 * Extract headings from markdown content
 * @param {string} markdown - The markdown content
 * @returns {Array} - Array of heading objects with id, text, and level
 */
const extractHeadings = (markdown) => {
  const headings = [];
  const lines = markdown.split('\n');
  let inCodeBlock = false;
  let codeBlockFence = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    if (trimmedLine.startsWith('```') || trimmedLine.startsWith('~~~')) {
      if (!inCodeBlock) {
        inCodeBlock = true;
        codeBlockFence = trimmedLine.substring(0, 3);
      } else if (trimmedLine.startsWith(codeBlockFence)) {
        inCodeBlock = false;
        codeBlockFence = '';
      }
      continue;
    }

    if (inCodeBlock) {
      continue;
    }

    const atxMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (atxMatch) {
      const level = atxMatch[1].length;
      const text = atxMatch[2].trim().replace(/#+\s*$/, '');
      const id = generateHeadingId(text);
      headings.push({ id, text, level });
      continue;
    }

    if (i < lines.length - 1) {
      const nextLine = lines[i + 1].trim();
      if (nextLine && /^=+$/.test(nextLine)) {
        const text = line.trim();
        if (text) {
          const id = generateHeadingId(text);
          headings.push({ id, text, level: 1 });
        }
        i++;
        continue;
      }

      if (nextLine && /^-+$/.test(nextLine)) {
        const text = line.trim();
        if (text) {
          const id = generateHeadingId(text);
          headings.push({ id, text, level: 2 });
        }
        i++;
        continue;
      }
    }
  }

  return headings;
};

export { extractHeadings, scrollToElement, generateHeadingId };
